"use client";

import { Loader, Modal, Skeleton } from "@mantine/core";
import {
  ContentGrid,
  EmptyState,
  Icon,
  MainButton,
  StatusBadge,
} from "@repo/dashboard-core";
import { useLocale, useTranslations } from "next-intl";
import { useCallback, useEffect, useState } from "react";

import { fetchEsim, fetchEsimTopUpPlans } from "@/lib/actions/esim";
import {
  esimLifecycle,
  type EsimCurrency,
  type EsimDetail,
  type EsimProduct,
} from "@/lib/types/esim";
import {
  formatBalanceAmount,
  formatEsimDate,
  orderDataAllowance,
} from "@/lib/utils/esim";

import { EsimInstall } from "./EsimInstall";
import { PlanCard } from "./PlanCard";

const LIFECYCLE_VARIANT = {
  ready: "info",
  installed: "warning",
  active: "success",
} as const;

export function EsimDetailModal({
  iccid,
  currency,
  onClose,
  onTopUp,
}: {
  iccid: string | null;
  currency: EsimCurrency;
  onClose: () => void;
  onTopUp: (product: EsimProduct, iccid: string) => void;
}) {
  const t = useTranslations("esim");
  const locale = useLocale();

  const [detail, setDetail] = useState<EsimDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState<EsimProduct[] | null>(null);
  const [loadingPlans, setLoadingPlans] = useState(false);
  // The API's reason for an empty list, not a guess from `plans.length`.
  const [plansAllowanceBlocked, setPlansAllowanceBlocked] = useState(false);

  useEffect(() => {
    if (!iccid) {
      setDetail(null);
      setPlans(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);

    fetchEsim(currency, iccid).then((result) => {
      if (cancelled) return;
      setDetail(result.success ? result.data : null);
      setError(result.success ? null : result.error);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [iccid, currency]);

  // DT One only accepts a recharge of the same coverage as the eSIM's current
  // plan, so the compatible list is derived from the plan it was bought with.
  const loadTopUpPlans = useCallback(async () => {
    const purchase = detail?.orders.find((order) => order.type === "purchase");
    if (!purchase) return;

    setLoadingPlans(true);
    const result = await fetchEsimTopUpPlans(currency, purchase.productId);
    setPlans(result.success ? result.data.products : []);
    setPlansAllowanceBlocked(result.success && result.data.allowanceBlocked);
    setLoadingPlans(false);
  }, [currency, detail]);

  const lifecycle = detail ? esimLifecycle(detail.esim) : null;

  return (
    <Modal
      opened={Boolean(iccid)}
      onClose={onClose}
      title={t("detail-title")}
      size="xl"
      radius="lg"
      centered
    >
      {loading && (
        <div className="space-y-4">
          <Skeleton height={180} radius="lg" />
          <Skeleton height={80} radius="lg" />
        </div>
      )}

      {!loading && error && (
        <EmptyState
          icon={<Icon name="alert" size={28} />}
          title={t("detail-error")}
          description={t("detail-error-description")}
        />
      )}

      {!loading && detail && (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-3">
            {lifecycle && (
              <StatusBadge
                label={t(`status-${lifecycle}`)}
                variant={LIFECYCLE_VARIANT[lifecycle]}
              />
            )}
            {detail.esim.installedAt && (
              <span className="text-base text-text-secondary">
                {t("installed-on", {
                  date: formatEsimDate(detail.esim.installedAt, locale),
                })}
              </span>
            )}
            {detail.esim.activatedAt && (
              <span className="text-base text-text-secondary">
                {t("activated-on", {
                  date: formatEsimDate(detail.esim.activatedAt, locale),
                })}
              </span>
            )}
          </div>

          <EsimInstall profile={detail.esim} />

          <Section title={t("section-balance")}>
            {detail.balance === null ? (
              <p className="text-base text-text-secondary">
                {t("balance-unavailable")}
              </p>
            ) : detail.balance.length === 0 ? (
              <p className="text-base text-text-secondary">
                {t("balance-empty")}
              </p>
            ) : (
              <ul className="space-y-2">
                {detail.balance.map((entry, index) => (
                  <li
                    key={`${entry.type}-${index}`}
                    className="flex items-baseline justify-between gap-3 rounded-xl bg-content-bg px-3 py-2 text-base"
                  >
                    <span className="font-medium text-text-primary">
                      {formatBalanceAmount(entry)}
                    </span>
                    <span className="text-text-secondary">
                      {entry.expiration_date
                        ? t("balance-expires", {
                            date: formatEsimDate(entry.expiration_date, locale),
                          })
                        : t("balance-not-started")}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title={t("section-history")}>
            <ul className="space-y-2">
              {detail.orders.map((order) => (
                <li
                  key={order.esimId}
                  className="flex flex-wrap items-baseline justify-between gap-2 rounded-xl bg-content-bg px-3 py-2 text-base"
                >
                  <span className="font-medium text-text-primary">
                    {order.productName}
                  </span>
                  <span className="text-text-secondary">
                    {t(`order-type-${order.type}`)}
                    {orderDataAllowance(order.benefits)
                      ? ` · ${orderDataAllowance(order.benefits)}`
                      : ""}
                    {order.status?.message ? ` · ${order.status.message}` : ""}
                  </span>
                </li>
              ))}
            </ul>
          </Section>

          <Section title={t("section-topup")}>
            {plans === null ? (
              <div className="flex flex-wrap items-center gap-3">
                <p className="flex-1 text-base text-text-secondary">
                  {t("topup-hint")}
                </p>
                <MainButton onClick={loadTopUpPlans} disabled={loadingPlans}>
                  {loadingPlans ? (
                    <Loader size={16} color="currentColor" />
                  ) : (
                    t("action-add-data")
                  )}
                </MainButton>
              </div>
            ) : plans.length === 0 ? (
              <p className="text-base text-text-secondary">
                {plansAllowanceBlocked
                  ? t("error-allowance")
                  : t("topup-no-plans")}
              </p>
            ) : (
              <ContentGrid cols={3} spacing="sm">
                {plans.map((plan) => (
                  <PlanCard
                    key={plan.id}
                    product={plan}
                    actionLabel={t("action-add-data")}
                    onSelect={(product) => iccid && onTopUp(product, iccid)}
                  />
                ))}
              </ContentGrid>
            )}
          </Section>
        </div>
      )}
    </Modal>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-2">
      <h4 className="text-base font-semibold text-text-primary">{title}</h4>
      {children}
    </section>
  );
}
